import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Injectable } from '@angular/core';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import {  CountryMaster } from 'src/app/shared/models/app.models';
import { lastValueFrom, Observable, of } from 'rxjs';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})


export class ApiService {
    //#region fields
    private _baseUrl?: string;
    //#endregion
    private readonly CACHE_VERSION = 'v2';

    private CACHE_EXPIRY_KEY = 'allLookupsExpiry';
    private LOOKUPS_VERSION_KEY = 'allLookupsVersion';
    private CACHE_EXPIRY_TIME = 1 * 60 * 60 * 1000; // 1 hour in milliseconds   
    // Variables to hold lookup data
    public countries: any[] = [];
    public currencies: any[] = [];
    public methodsOfPayment: any[] = [];
    public categories: any[] = [];
    public subCategories: any[] = [];
    public subSubCategories: any[] = [];
    public categoryTree: any[] = [];
    public termsOfPayment: any[] = [];
    public preQualifications: any[] = [];
    public banks: any[] = [];
    public documentsMaster: any[] = [];
    
    

    //#region Ctor 
    constructor(private _http: HttpClient, 
       private _loaderService?: LoaderService,
       private _userService?: UserService,
      ) {
        // Clear old cache format on service initialization
        this.clearOldCacheFormat();
    }
    //#endregion

    private get cacheKey(): string {
      return `allLookups_${this.CACHE_VERSION}_${this._userService?.user?.Id || 'anon'}`;
    }


    public setBaseUrl(url: string): void {
      this._baseUrl = url;
    }

    //#region Private actions
    private buildUrl(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null): string {
      if(withPrefix && prefix)return this._baseUrl + '/' + prefix + '/'+ controllerName + '/' + actionName
      return this._baseUrl + '/' + controllerName + '/' + actionName
    }

    // Clear old cache format
    private clearOldCacheFormat(): void {
      // Check if old format exists (without versioning)
      const oldCacheKey = 'allLookups';
      if (localStorage.getItem(oldCacheKey)) {
        console.log('Clearing old cache format...');
        localStorage.removeItem(oldCacheKey);
        localStorage.removeItem('allLookupsExpiry');
      }
    }

    /**
     * Clears all lookup-related data from localStorage.
     * Used when server lookups version changes so vendors get fresh data.
     */
    public clearLookupsCache(): void {
      try {
        localStorage.removeItem(this.CACHE_EXPIRY_KEY);
        localStorage.removeItem(this.LOOKUPS_VERSION_KEY);
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key === 'allLookups' || key.startsWith('allLookups_'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(k => localStorage.removeItem(k));
      } catch (e) {
        console.warn('clearLookupsCache failed', e);
      }
    }

    // Method to check if cache is valid
    private isCacheValid(): boolean {
      const expiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (expiry) {
        const expiryDate = new Date(expiry);
        return new Date().getTime() < expiryDate.getTime();
      }
      return false;
    }
    //#endregion

    //#region GET
    public getByUrl(url: string, keyValueParams?: {}, options?: any) {
      let queryString = '';
      if (keyValueParams) {
        const keys = Object.keys(keyValueParams);
        keys.forEach((key, index) => {
          queryString += index === 0 ? '?' : '&';
          queryString += `${key}=${keyValueParams[key as keyof typeof keyValueParams]}`;
        });
      }
    
      // Ensure options are initialized
      if (!options) {
        options = {};
      }
    
      // Set default headers, including the Authorization header
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Content-Type': 'application/json', // Content-Type may not be necessary for GET, but including it ensures consistency
          'Authorization': 'Bearer ' +     localStorage.getItem('QNBNToken')
          // Add your method to get the token
        });
      } else {
        options.headers = new HttpHeaders({
          ...options.headers,  // Retain existing headers if there are any
          'Authorization': 'Bearer ' +   localStorage.getItem('QNBNToken')
        });
      }
    
      // Make the GET request with the headers
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.get(url + queryString, options);
    }
    
  
    
    public get(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, keyValueParams?: {}, options?: any) {


        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.getByUrl(url, keyValueParams, options);


    }
    //#endregion

    //#region POST
    public postByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
      let queryString = '';
      if (keyValueParams) {
        const keys = Object.keys(keyValueParams);
        keys.forEach((key, index) => {
          queryString += index === 0 ? '?' : '&';
          queryString += `${key}=${keyValueParams[key as keyof typeof keyValueParams]}`;
        });
      }

      if (!options) {
        options = {};
      }
      
      // Set default headers, including the Authorization header
      if (!options.headers) {
        options.headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
        });
      } else {
        // Merge existing headers with Authorization header
        const existingHeaders: { [key: string]: string } = {};
        options.headers.keys().forEach(key => {
          existingHeaders[key] = options.headers.get(key) || '';
        });
        existingHeaders['Authorization'] = 'Bearer ' + localStorage.getItem('QNBNToken');
        options.headers = new HttpHeaders(existingHeaders);
      }

      console.log('Final URL:', url + queryString);
      console.log('Payload:', payload);
      console.log('Options:', options);
      console.log('Headers:', options.headers.keys().map(key => `${key}: ${options.headers.get(key)}`));

      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.post(url + queryString, payload, options);
    }

    public post(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.postByUrl(url, payload, keyValueParams, options);
    }

    public postMultipart(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, allFiles: any[] = []) {
      const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
      const formData: FormData = new FormData();
      const keys = Object.keys(payload);
      keys.forEach((key, index) => {
        formData.append(key, payload[key as keyof typeof payload]);
      });

      if (allFiles?.length > 0){
        allFiles.forEach((f,index) => {
          // if (f.File){
            formData.append(`Files[${index}].typeAttachment`, f.TypeAttachment);
            formData.append(`Files[${index}].attachmentTypeEnum`, f.AttachmentTypeEnum);
            formData.append(`Files[${index}].file`, f.File);
          // }
        });
      }

      // formData.append("IdExpiryDate", payload.IdExpiryDate);
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.post(url , formData);
    }

    public putMultipart(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any) {
      const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
      const formData: FormData = new FormData();
      const keys = Object.keys(payload);
      keys.forEach((key, index) => {
        formData.append(key, payload[key as keyof typeof payload]);
      });
      // formData.append("IdExpiryDate", payload.IdExpiryDate);
      // Let the interceptor handle mock data when DISABLE_BACKEND is true
      return this._http.put(url , formData);
    }

    // public uploadFiles(allFiles: FileRequest[]) {
    //   const formData = new FormData();
    //  // formData.append('Files', allFiles);
    //  allFiles.forEach((f,index) => {
    //   formData.append(`Files[${index}].typeAttachment`, f.TypeAttachment);
    //   formData.append(`Files[${index}].file`, f.File);
    //  });
    //   // formData.append("Files[2].typeAttachment", "tt1");
    //   // formData.append("Files[2].file", allFiles[0].File);
    //   // formData.append("Files[3].typeAttachment", "tt2");
    //   // formData.append("Files[3].file", allFiles[1].File);
    //   return this._http.post<any>('https://localhost:7237/api/Request/SubmitLicenceRequest', formData);
    // }
    //#endregion

    //#region PUT
    public putByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        
        if (!options) {
          options = {};
        }
        
        // Set default headers, including the Authorization header
        if (!options.headers) {
          options.headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        } else {
          // Merge existing headers with Authorization header
          const existingHeaders: { [key: string]: string } = {};
          options.headers.keys().forEach(key => {
            existingHeaders[key] = options.headers.get(key) || '';
          });
          existingHeaders['Authorization'] = 'Bearer ' + localStorage.getItem('QNBNToken');
          options.headers = new HttpHeaders(existingHeaders);
        }
        
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.put(url + queryString, payload, options);
    }

    public put(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.putByUrl(url, payload, keyValueParams, options);
    }
    //#endregion

    //#region PATCH
    public pacthByUrl(url: string, payload: any, keyValueParams?: {}, options?: any) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.patch(url + queryString, payload, options);
    }

    public patch(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null, payload: any, keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.pacthByUrl(url, payload, keyValueParams, options);
    }
    //#endregion

    //#region DELETE
    public deleteByUrl(url, keyValueParams, options) {
        let queryString = '';
        if(keyValueParams) {
          const keys = Object.keys(keyValueParams);
          keys.forEach((key, index) => {
            if(index === 0) {
              queryString += '?' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            } else {
              queryString += '&' + key + '=' + keyValueParams[key as keyof typeof keyValueParams];
            }
          });
        }
        
        if (!options) {
          options = {};
        }
        
        // Set default headers, including the Authorization header
        if (!options.headers) {
          options.headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('QNBNToken')
          });
        } else {
          // Merge existing headers with Authorization header
          const existingHeaders: { [key: string]: string } = {};
          options.headers.keys().forEach(key => {
            existingHeaders[key] = options.headers.get(key) || '';
          });
          existingHeaders['Authorization'] = 'Bearer ' + localStorage.getItem('QNBNToken');
          options.headers = new HttpHeaders(existingHeaders);
        }
        
        // Let the interceptor handle mock data when DISABLE_BACKEND is true
        return this._http.delete(url + queryString, options);
    }
    public delete(controllerName: string, actionName: string, withPrefix: boolean, prefix: string | null,  keyValueParams?: {}, options?: any) {
        const url = this.buildUrl(controllerName, actionName, withPrefix, prefix);
        return this.deleteByUrl(url, keyValueParams, options);
    }
    //#endregion



    //#region lookups

  




  // Method to fetch lookups from the backend
  private async fetchLookupsFromBackend(): Promise<any> {
    this._baseUrl = environment.API_URL;
    const url = this.buildUrl(AppConstants.CONTROLLERS.Lookups, 'all-lookups', true, environment.APIs_PREFIX);
    const data = await lastValueFrom(this.getByUrl(url));
    return data;
  }

  // Fetches the lookups version from the backend (changes when any lookup is modified).
  private async fetchLookupsVersion(): Promise<number | null> {
    try {
      this._baseUrl = environment.API_URL;
      const url = this.buildUrl(AppConstants.CONTROLLERS.Lookups, 'version', true, environment.APIs_PREFIX);
      const res: any = await lastValueFrom(this.getByUrl(url));
      const v = res?.version ?? res?.Version;
      return v != null ? Number(v) : null;
    } catch {
      return null;
    }
  }

  // Main method to load all lookups (from cache or backend). Uses server version to invalidate cache when lookups change.
  public async loadAllLookups(): Promise<void> {
    this.loadCurrenciesFromLocalStorage();

    const serverVersion = await this.fetchLookupsVersion();
    const storedVersion = localStorage.getItem(this.LOOKUPS_VERSION_KEY);
    const versionMatch = serverVersion != null && storedVersion !== null && String(serverVersion) === storedVersion;

    if (versionMatch && this.isCacheValid()) {
      const cachedData = localStorage.getItem(this.cacheKey);
      if (cachedData) {
        this.assignLookups(JSON.parse(cachedData));
        return;
      }
    }

    if (serverVersion != null && String(serverVersion) !== storedVersion) {
      this.clearLookupsCache();
    }

    const data = await this.fetchLookupsFromBackend();
    localStorage.setItem(this.cacheKey, JSON.stringify(data));
    localStorage.setItem(this.CACHE_EXPIRY_KEY, new Date(Date.now() + this.CACHE_EXPIRY_TIME).toISOString());
    if (serverVersion != null) {
      localStorage.setItem(this.LOOKUPS_VERSION_KEY, String(serverVersion));
    }
    this.assignLookups(data);
    this.loadCurrenciesFromLocalStorage();
  }

  // Load currencies from local storage (allLookups_v2_anon)
  private loadCurrenciesFromLocalStorage(): void {
    try {
      const cacheKey = `allLookups_${this.CACHE_VERSION}_anon`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const data = JSON.parse(cachedData);
        if (data.currencies && Array.isArray(data.currencies) && data.currencies.length > 0) {
          this.currencies = data.currencies;
          console.log('Currencies loaded from local storage:', this.currencies);
        }
      }
    } catch (error) {
      console.error('Error loading currencies from local storage:', error);
    }
  }

  // Method to assign lookups to variables
  private assignLookups(data: any): void {
    this.countries = data.countries || [];
    // Don't override currencies if they were loaded from local storage
    if (!this.currencies || this.currencies.length === 0) {
      this.currencies = data.currencies || [];
    }
    this.methodsOfPayment = data.methodsOfPayment || [];
    this.categories = data.categories || [];
    this.subCategories = data.subCategories || [];
    this.subSubCategories = data.subSubCategories || [];
    this.categoryTree = data.categoryTree || [];
    this.termsOfPayment = data.termsOfPayment || [];
    this.preQualifications = data.preQualifications || [];
    this.banks = data.banks || [];
    this.documentsMaster = data.documentsMaster || [];

    // Deduplicate documentMaster on key properties
    this.documentsMaster = (data.documentsMaster || []).filter((doc, index, self) =>
      index === self.findIndex(d =>
        d.documentName === doc.documentName &&
        d.parmDocumentFor === doc.parmDocumentFor &&
        d.processName === doc.processName
      )
    );
  }




    //#endregion
}
